import { useMemo, useRef, useState } from "react";
import { z } from "zod";

export const liveTimelineSchema = z
  .object({
    entries: z.array(z.string().max(20_000)).min(1).max(11),
    cursor: z.number().int().min(0)
  })
  .refine((value) => value.cursor < value.entries.length);
export type LiveTimeline = z.infer<typeof liveTimelineSchema>;
export const liveHistoriesSchema = z.record(z.string(), liveTimelineSchema);

export function appendHistory(history: LiveTimeline, text: string, group = false): LiveTimeline {
  if (history.entries[history.cursor] === text) return history;
  const entries = history.entries.slice(0, history.cursor + 1);
  if (group && history.cursor > 0) entries[entries.length - 1] = text;
  else entries.push(text);
  if (entries.length > 1 && entries.at(-1) === entries.at(-2)) entries.pop();
  const bounded = entries.slice(-11);
  return { entries: bounded, cursor: bounded.length - 1 };
}

// A conventional bounded timeline; typing within 800ms is a single edit group.
// Blur, AI replacement, navigation, and saving end the typing group.
export function useLiveHistory<K extends string>(
  initial: Record<K, string>,
  restored?: Record<string, LiveTimeline>
) {
  const [histories, setHistories] = useState<Record<K, LiveTimeline>>(
    () =>
      Object.fromEntries(
        (Object.keys(initial) as K[]).map((key) => {
          const prior = restored?.[key];
          return [
            key,
            prior?.entries[prior.cursor] === initial[key]
              ? prior
              : { entries: [initial[key]], cursor: 0 }
          ];
        })
      ) as Record<K, LiveTimeline>
  );
  const state = useRef(histories);
  const groups = useRef<Partial<Record<K, number>>>({});
  const values = useMemo(
    () =>
      Object.fromEntries(
        (Object.keys(histories) as K[]).map((key) => [
          key,
          histories[key].entries[histories[key].cursor]!
        ])
      ) as Record<K, string>,
    [histories]
  );
  const valuesRef = useRef(values);
  const commit = (next: Record<K, LiveTimeline>) => {
    state.current = next;
    valuesRef.current = Object.fromEntries(
      (Object.keys(next) as K[]).map((key) => [key, next[key].entries[next[key].cursor]!])
    ) as Record<K, string>;
    setHistories(next);
  };
  const finish = (key: K) => {
    delete groups.current[key];
  };
  return {
    histories,
    values,
    valuesRef,
    finish,
    change: (key: K, text: string, typing = false) => {
      const now = Date.now();
      const previous = groups.current[key];
      const next = appendHistory(
        state.current[key],
        text,
        typing && previous !== undefined && now - previous < 800
      );
      if (typing) groups.current[key] = now;
      else finish(key);
      commit({ ...state.current, [key]: next });
    },
    move: (key: K, step: -1 | 1) => {
      finish(key);
      const history = state.current[key];
      const cursor = Math.max(0, Math.min(history.entries.length - 1, history.cursor + step));
      commit({ ...state.current, [key]: { ...history, cursor } });
      return history.entries[cursor]!;
    },
    reset: (patch: Partial<Record<K, string>>) => {
      const next = { ...state.current };
      for (const key of Object.keys(patch) as K[]) {
        finish(key);
        next[key] = { entries: [patch[key]!], cursor: 0 };
      }
      commit(next);
    }
  };
}
